import { Seo } from '../components/Seo';
import Logo from '../components/Logo';
import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowLeft, Scale, AlertTriangle } from 'lucide-react';

const SECTIONS = [
  {
    title: '1. About the Service',
    paras: [
      'VerifAEye is a documentation and analysis tool that helps you photograph, organise, and describe property and contents for the purpose of preparing insurance claims and personal records. The Service uses artificial intelligence to analyse images you upload and generate descriptions, condition notes, and reports.',
    ],
  },
  {
    title: '3. Eligibility and Accounts',
    paras: [
      'You must be at least 18 years old and capable of forming a binding contract. You are responsible for the accuracy of your account information and for keeping your login credentials secure. You are responsible for all activity under your account.',
    ],
  },
  {
    title: '4. Your Content and Storage',
    paras: [
      'You retain ownership of all images and information you upload ("Your Content"). You grant us a limited licence to process Your Content solely to provide the Service (including transmitting images to our AI analysis providers).',
      'The Service stores your images in your own Google Drive account where you connect one. You are responsible for your Google account, its security, and its available storage. We do not maintain a copy of your images on our servers beyond transient processing. If you disconnect Google Drive or delete files from it, we cannot recover them.',
    ],
  },
  {
    title: '5. Acceptable Use',
    paras: ['You must not use the Service to:'],
    list: [
      "Create, alter, or present evidence in a false or misleading way, including in support of a fraudulent or exaggerated insurance claim. Insurance fraud is a criminal offence.",
      "Upload content that is unlawful, infringes another person's rights, or contains malware.",
      'Reverse engineer, scrape, resell, or interfere with the Service.',
      'Circumvent usage limits or payment requirements.',
    ],
    after: ['We may suspend or terminate accounts engaged in prohibited conduct.'],
  },
  {
    title: '6. Credits and Payments',
    paras: [
      'The Service offers a free tier and paid credit packs. Payments are processed by Stripe in Australian dollars; we do not store your card details. Credits are consumed when analyses are performed and, except as required by law, are non-transferable.',
      'Nothing in these Terms excludes, restricts, or modifies any consumer guarantee, right, or remedy you have under the Australian Consumer Law that cannot lawfully be excluded. If the Service fails to meet a consumer guarantee, you are entitled to the remedies provided by law.',
    ],
  },
  {
    title: '7. Third-Party Services',
    paras: [
      'The Service integrates with third-party services including Google Drive (storage), Stripe (payments), and AI model providers (image analysis). Your use of those services is subject to their own terms and privacy policies. We are not responsible for the acts or omissions of third-party providers, to the extent permitted by law.',
    ],
  },
  {
    title: '8. Intellectual Property',
    paras: [
      'The Service, including its software, design, branding, and content (excluding Your Content), is owned by us or our licensors. These Terms do not transfer any of our intellectual property to you.',
    ],
  },
  {
    title: '9. Privacy',
    paras: [
      'Our collection and handling of personal information is described in our Privacy Policy. By using the Service you consent to that handling.',
    ],
    privacyLink: true,
  },
  {
    title: '10. Liability',
    paras: ['To the maximum extent permitted by law, and subject to the Australian Consumer Law:'],
    list: [
      'The Service is provided "as is" and we do not warrant it will be uninterrupted or error-free.',
      'We are not liable for indirect or consequential loss, loss of profit, or loss arising from your reliance on AI-generated outputs without review, or from decisions made by insurers or other third parties.',
      'Where liability cannot be excluded but can be limited, our liability is limited to re-supplying the Service or refunding the amount you paid for the affected credits.',
    ],
  },
  {
    title: '11. Termination',
    paras: [
      'You may close your account at any time. We may suspend or terminate the Service or your account for breach of these Terms, unlawful conduct, or where required by law. Unused free-tier allowances lapse on termination; statutory rights regarding paid credits are unaffected.',
    ],
  },
  {
    title: '12. Changes to the Service and Terms',
    paras: [
      'We may update the Service and these Terms from time to time. Material changes will be notified via the Service or email, and continued use after notice constitutes acceptance. The current version will always be available at this page.',
    ],
  },
  {
    title: '13. Governing Law',
    paras: [
      'These Terms are governed by the laws of Queensland, Australia, and you submit to the non-exclusive jurisdiction of its courts.',
    ],
  },
];

const TermsOfService = () => {
  const effectiveDate = '18 July 2026';

  return (
    <div className="min-h-screen bg-slate-50">
      <Seo
        title="Terms of Service | VerifAEye"
        description="Terms of Service for VerifAEye — AI-assisted insurance claim documentation for Australian policyholders."
        path="/terms"
      />
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Logo size="sm" />
          </Link>
          <Link to="/">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden" data-testid="terms-content">
          {/* Title Section */}
          <div className="bg-gradient-to-r from-slate-900 to-slate-800 p-8 text-white">
            <h1 className="text-3xl font-bold mb-2 flex items-center gap-3">
              <Scale className="w-8 h-8" />
              Terms of Service
            </h1>
            <p className="text-slate-300">Effective date: {effectiveDate}</p>
          </div>

          <div className="p-8 space-y-8">
            <section>
              <p className="text-slate-600 leading-relaxed">
                These Terms of Service ("Terms") govern your use of VerifAEye (the "Service"), operated by
                Bruce Lawrence Visser trading as Symbionic (ABN 88 549 501 774) ("we", "us", "our").
                By creating an account or using the Service you agree to these Terms.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-slate-900 mb-3">{SECTIONS[0].title}</h2>
              <p className="text-slate-600 leading-relaxed">{SECTIONS[0].paras[0]}</p>
            </section>

            {/* Important Disclaimers - highlighted */}
            <section className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h2 className="text-xl font-semibold text-amber-900 mb-4 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5" />
                2. Important Disclaimers
              </h2>
              <div className="space-y-3 text-sm text-amber-900">
                <p><strong>We are not your insurer, broker, adviser, or advocate.</strong> The Service does not provide insurance, legal, or financial advice. Outputs generated by the Service are informational aids only.</p>
                <p><strong>No guarantee of claim outcomes.</strong> Using the Service does not guarantee that any insurance claim will be accepted, paid, or settled at any particular value. Claim decisions are made solely by your insurer under your policy terms.</p>
                <p><strong>AI outputs may contain errors.</strong> AI-generated descriptions and analyses can be incomplete or inaccurate. You are responsible for reviewing all outputs and correcting them before relying on them or submitting them to any third party.</p>
              </div>
            </section>

            {SECTIONS.slice(1).map((s) => (
              <section key={s.title}>
                <h2 className="text-xl font-semibold text-slate-900 mb-3">{s.title}</h2>
                {s.paras.map((p, i) => (
                  <p key={i} className="text-slate-600 leading-relaxed mb-3">
                    {p}{' '}
                    {s.privacyLink && i === 0 && (
                      <Link to="/privacy" className="text-teal-600 hover:text-teal-700 underline">
                        Read our Privacy Policy
                      </Link>
                    )}
                  </p>
                ))}
                {s.list && (
                  <ul className="list-disc pl-6 space-y-2 text-slate-600 leading-relaxed mb-3">
                    {s.list.map((item, i) => (
                      <li key={i}>{item}</li>
                    ))}
                  </ul>
                )}
                {s.after && s.after.map((p, i) => (
                  <p key={i} className="text-slate-600 leading-relaxed">{p}</p>
                ))}
              </section>
            ))}

            <section>
              <h2 className="text-xl font-semibold text-slate-900 mb-3">14. Contact</h2>
              <div className="grid gap-4 sm:grid-cols-[1fr_auto] items-start">
                <div className="space-y-1.5 text-slate-600 leading-relaxed">
                  <p>
                    Questions about these Terms:{' '}
                    <a href="mailto:brucev@symbionic.com.au" className="text-teal-600 hover:text-teal-700 underline" data-testid="contact-email-terms">
                      brucev@symbionic.com.au
                    </a>
                  </p>
                  <p>WhatsApp: scan the QR to message Bruce directly.</p>
                  <p className="pt-2">
                    For more insurance information, tips, and definitions — free of charge — visit{' '}
                    <a href="https://symbionic.com.au" className="text-teal-600 hover:text-teal-700 underline" target="_blank" rel="noopener noreferrer" data-testid="symbionic-link-terms">
                      Symbionic
                    </a>.
                  </p>
                </div>
                <picture>
                  <source srcSet="/whatsapp-qr.webp" type="image/webp" />
                  <img src="/whatsapp-qr-320.png" width="344" height="320" alt="WhatsApp QR code to message Bruce" className="w-28 h-28 rounded-lg border border-slate-200 bg-white p-1" data-testid="whatsapp-qr-terms" />
                </picture>
              </div>
            </section>
          </div>
        </div>

        <div className="text-center py-8">
          <p className="text-slate-400 text-sm">VerifAEye — The Proof is in the Detail</p>
        </div>
      </main>
    </div>
  );
};

export default TermsOfService;
